/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef } from 'react';
import { useTheme } from '@veneer/core';
import { useDispatch, useSelector } from 'react-redux';

import CategoryList from '../../components/category-list';

import { DeviceDetailsContainer } from '../../components/device-item/styled';

import * as T from '../../types/devices';
import { OperationStatus } from '../../types';
import ApiError from '../../types/error';

import useDeviceCategoryMapping, {
  UseDeviceCategoryMappingResult
} from '../../hooks/useDeviceCategoryMapping';
import { usePolling } from '../../hooks/usePolling';

import {
  getDeviceTelemetryRequest,
  getDeviceTelemetryCleanup
} from '../../features/telemetry/device-telemetry-slice';
import { RootState } from '../../store';

const DeviceDetailsViewer: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const hasInitiatedTelemetry = useRef(false);

  const {
    category: categoryDeviceTypeMap,
    isLoading: isCategoryLoading,
    error
  }: UseDeviceCategoryMappingResult = useDeviceCategoryMapping();

  const {
    devices
  }: { devices: Record<string, T.Device[] | string | ApiError> | null } =
    useSelector((state: RootState) => state.DevicesReducer);

  const { getDeviceTelemetry } = useSelector(
    (state: RootState) => state.DeviceTelemetryReducer
  );

  const pollingCallback = useCallback(() => {
    dispatch(getDeviceTelemetryRequest());
  }, [dispatch]);

  const { startPolling, stopPolling } = usePolling({
    callback: pollingCallback,
    interval: 600000, // 10 minutes
    startImmediately: false
  });

  const shouldSkipPolling = () => {
    // Don't start polling if devices is null/undefined (no mapping requests made yet)
    if (!devices) {
      return true;
    }

    // Check if we have any valid device arrays in devices
    const hasValidDevices = Object.values(devices).some(
      (devicesList) => Array.isArray(devicesList) && devicesList.length > 0
    );

    if (hasValidDevices) {
      return false; // Don't skip polling - we have devices to poll for
    }

    // If no valid devices, only skip if polling is already in progress
    const isPollingInProgress =
      getDeviceTelemetry.status === OperationStatus.RUNNING;
    return isPollingInProgress; // Skip only if already polling
  };

  useEffect(() => {
    if (shouldSkipPolling()) return;

    // Only trigger telemetry request if we actually have valid devices
    const hasValidDevices =
      devices &&
      Object.values(devices).some(
        (devicesList) => Array.isArray(devicesList) && devicesList.length > 0
      );

    // Only start polling once per session when devices become available
    if (hasValidDevices && !hasInitiatedTelemetry.current) {
      hasInitiatedTelemetry.current = true;
      // Start polling when devices become available for the first time
      // startPolling() will make the initial request automatically
      stopPolling();
      startPolling();
    }
  }, [devices]);

  // Cleanup telemetry data and stop polling when component unmounts
  useEffect(() => {
    return () => {
      stopPolling();
      dispatch(getDeviceTelemetryCleanup());
      // Reset the ref when component unmounts
      hasInitiatedTelemetry.current = false;
    };
  }, []);

  const filteredCategoryTypesList = Object.keys(
    categoryDeviceTypeMap
  ) as T.CategoryType[];

  return (
    <DeviceDetailsContainer
      data-testid="react-device-management-mfe-device-details-viewer-container"
      $veneerTheme={theme}
    >
      <CategoryList
        isCategoryLoading={isCategoryLoading}
        error={error}
        categoryDeviceTypeMap={categoryDeviceTypeMap}
        filteredCategoryTypes={filteredCategoryTypesList}
      />
    </DeviceDetailsContainer>
  );
};

export default DeviceDetailsViewer;
